import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import { isNativeApp } from './services/native';
import { ToastProvider } from './components/Toast';

// Eagerly load only the shell routes (shown before auth is resolved)
import SplashScreen from './pages/SplashScreen';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';

// Lazy-load every other page — code-split so users only download what they visit.
// Dashboard + ChatInterface are the two hottest routes so they get their own chunks.
const RoleSelectionPage      = lazy(() => import('./pages/RoleSelectionPage'));
const Dashboard              = lazy(() => import('./pages/Dashboard'));
const ConnectDashboard       = lazy(() => import('./pages/ConnectDashboard'));
const OnboardingPage         = lazy(() => import('./pages/OnboardingPage'));
const ChatInterface          = lazy(() => import('./pages/ChatInterface'));
const FAQPage                = lazy(() => import('./pages/FAQPage'));
const HelpPage               = lazy(() => import('./pages/HelpPage'));
const AdminAnalytics         = lazy(() => import('./pages/AdminAnalytics'));
const RelationshipProfilesPage = lazy(() => import('./pages/RelationshipProfilesPage'));
const MemoriesDashboard      = lazy(() => import('./pages/MemoriesDashboard'));
const VoiceCloningPage       = lazy(() => import('./pages/VoiceCloningPage'));
const PrivacyPolicy          = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService         = lazy(() => import('./pages/TermsOfService'));
const ContactPage            = lazy(() => import('./pages/ContactPage'));
const PricingPage            = lazy(() => import('./pages/PricingPage'));
const SettingsPage           = lazy(() => import('./pages/SettingsPage'));
const InvitationPage         = lazy(() => import('./pages/InvitationPage'));
const ForgotPasswordPage     = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage      = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage        = lazy(() => import('./pages/VerifyEmailPage'));
const LandingPage            = lazy(() => import('./pages/LandingPage'));

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Minimal inline spinner shown while lazy chunks load
const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
    <div className="spinner" />
  </div>
);

function RequireAuth({ children, isAuthenticated }) {
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return children;
}

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    // Splash is for returning (signed-in) users opening the app. Anonymous
    // visitors get the landing page instantly — the brand film plays on the
    // login screen instead, so marketing traffic never waits on an intro.
    if (!localStorage.getItem('token')) return false;
    return !sessionStorage.getItem('hasSeenSplash');
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [preferredView, setPreferredView] = useState(null);

  useEffect(() => {
    const validateAuth = async () => {
      const token    = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      const savedView = localStorage.getItem('preferredView');

      if (savedView) setPreferredView(savedView);

      if (token && userData) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            setIsAuthenticated(true);
            setUser(JSON.parse(userData));
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch {
          // Network error — allow offline access with cached data
          setIsAuthenticated(true);
          setUser(JSON.parse(userData));
        }
      }

      setAuthChecked(true);
    };

    validateAuth();
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  const handleSwitchView = (view) => setPreferredView(view);
  const handleRoleSelect  = (role) => setPreferredView(role);

  const handleSplashComplete = () => {
    sessionStorage.setItem('hasSeenSplash', 'true');
    setShowSplash(false);
  };

  const getDefaultRoute = () => {
    if (!isAuthenticated) return '/login';
    if (!preferredView) return '/choose-view';
    return preferredView === 'connect' ? '/connect' : '/dashboard';
  };

  if (showSplash) return <SplashScreen onComplete={handleSplashComplete} />;
  if (!authChecked) return null;

  return (
    <ErrorBoundary>
    <ToastProvider>
    <Router>
      <div className="App">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public ───────────────────────────────────────────── */}
            <Route path="/"
              element={isAuthenticated
                ? <Navigate to={getDefaultRoute()} />
                : isNativeApp()
                  ? <Navigate to="/login" replace />
                  : <LandingPage />} />
            {/* Palette test drive — same landing page, earth color scheme */}
            <Route path="/preview/earth"
              element={<div className="theme-earth"><LandingPage /></div>} />
            <Route path="/login"
              element={isAuthenticated
                ? <Navigate to={getDefaultRoute()} />
                : <LoginPage onLogin={handleLogin} />}
            />
            <Route path="/signup"
              element={isAuthenticated
                ? <Navigate to={getDefaultRoute()} />
                : <SignupPage onLogin={handleLogin} />}
            />
            <Route path="/privacy"  element={<PrivacyPolicy />} />
            <Route path="/terms"    element={<TermsOfService />} />
            <Route path="/contact"  element={<ContactPage />} />
            <Route path="/pricing"  element={<PricingPage />} />
            <Route path="/invite/:token" element={<InvitationPage onLogin={handleLogin} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />

            {/* ── Role selection ────────────────────────────────────── */}
            <Route path="/choose-view"
              element={isAuthenticated
                ? (preferredView
                    ? <Navigate to={preferredView === 'connect' ? '/connect' : '/dashboard'} />
                    : <RoleSelectionPage user={user} onRoleSelect={handleRoleSelect} />)
                : <Navigate to="/login" />}
            />

            {/* ── Protected ────────────────────────────────────────── */}
            <Route path="/dashboard"
              element={<RequireAuth isAuthenticated={isAuthenticated}>
                <Dashboard user={user} onLogout={handleLogout} onSwitchView={handleSwitchView} />
              </RequireAuth>} />

            <Route path="/connect"
              element={<RequireAuth isAuthenticated={isAuthenticated}>
                <ConnectDashboard user={user} onLogout={handleLogout} onSwitchView={handleSwitchView} />
              </RequireAuth>} />

            <Route path="/onboarding"
              element={<RequireAuth isAuthenticated={isAuthenticated}>
                <OnboardingPage user={user} onLogout={handleLogout} />
              </RequireAuth>} />

            <Route path="/chat/:replicaId"
              element={<RequireAuth isAuthenticated={isAuthenticated}>
                <ChatInterface user={user} onLogout={handleLogout} />
              </RequireAuth>} />

            <Route path="/faq"
              element={<RequireAuth isAuthenticated={isAuthenticated}><FAQPage /></RequireAuth>} />

            <Route path="/help"
              element={<RequireAuth isAuthenticated={isAuthenticated}><HelpPage /></RequireAuth>} />

            <Route path="/admin/analytics"
              element={<RequireAuth isAuthenticated={isAuthenticated}><AdminAnalytics /></RequireAuth>} />

            <Route path="/relationship-profiles/:replicaId"
              element={<RequireAuth isAuthenticated={isAuthenticated}>
                <RelationshipProfilesPage user={user} />
              </RequireAuth>} />

            <Route path="/memories/:replicaId"
              element={<RequireAuth isAuthenticated={isAuthenticated}>
                <MemoriesDashboard user={user} />
              </RequireAuth>} />

            <Route path="/voice-cloning/:replicaId"
              element={<RequireAuth isAuthenticated={isAuthenticated}>
                <VoiceCloningPage user={user} />
              </RequireAuth>} />

            <Route path="/settings"
              element={<RequireAuth isAuthenticated={isAuthenticated}>
                <SettingsPage user={user} onLogout={handleLogout} />
              </RequireAuth>} />

            <Route path="*" element={<Navigate to={getDefaultRoute()} />} />
          </Routes>
        </Suspense>
      </div>
    </Router>
    </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
